import { Button, CopyButton } from '@mantine/core';
import { IconBrandApple, IconBrandGoogle, IconBrandWindows, IconLink } from '@tabler/icons-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import useIsMobile from 'src/Hooks/Shared/useIsMobile';
import { trackPlanningSubscribeClicked } from 'src/Services/Mixpanel/events';

import styles from './CalendarSubscribeOptions.module.scss';
import { Props } from './CalendarSubscribeOptions.types';

type Provider = 'apple' | 'google' | 'outlook';

const detectPlatform = (): 'apple' | 'android' | 'other' => {
  if (typeof navigator === 'undefined') return 'other';
  const ua = navigator.userAgent;
  if (/iPhone|iPad|iPod|Macintosh/.test(ua)) return 'apple';
  if (/Android/.test(ua)) return 'android';
  return 'other';
};

const CalendarSubscribeOptions = ({ icalUrl, layout = 'modal', context = 'app' }: Props) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile(768);

  // Apple requires the webcal:// scheme for a real subscription; https:// only imports the file once.
  const webcalUrl = icalUrl.replace(/^https?:\/\//, 'webcal://');
  const appleUrl = webcalUrl;
  const googleUrl = `https://calendar.google.com/calendar/render?cid=${encodeURIComponent(webcalUrl)}`;
  const outlookUrl = `https://outlook.live.com/calendar/0/addfromweb?url=${encodeURIComponent(icalUrl)}&name=${encodeURIComponent('ShareHeritage')}`;

  const platform = useMemo(detectPlatform, []);

  const providers: {
    key: Provider;
    label: string;
    icon: typeof IconBrandApple;
    href: string;
  }[] = [
    {
      key: 'apple',
      label: t('calendarSync.appleLabel', 'Agenda iPhone / Mac'),
      icon: IconBrandApple,
      href: appleUrl,
    },
    {
      key: 'google',
      label: t('calendarSync.googleLabel', 'Google Agenda'),
      icon: IconBrandGoogle,
      href: googleUrl,
    },
    {
      key: 'outlook',
      label: t('calendarSync.outlookLabel', 'Outlook'),
      icon: IconBrandWindows,
      href: outlookUrl,
    },
  ];

  const orderedProviders = useMemo(() => {
    if (platform === 'apple') {
      return [providers[0], providers[1], providers[2]];
    }
    // Android or other: Google first
    return [providers[1], providers[0], providers[2]];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [platform, icalUrl]);

  const openProvider = (provider: Provider, href: string) => {
    trackPlanningSubscribeClicked({ provider, context });
    window.open(href, '_blank', 'noopener,noreferrer');
  };

  const isPage = layout === 'page';

  return (
    <div className={`${styles.container} ${isPage ? styles.page : ''}`}>
      <div className={styles.buttons}>
        {orderedProviders.map((provider, index) => (
          <div key={provider.key}>
            <Button
              leftSection={<provider.icon size={20} />}
              variant={index === 0 && isPage ? 'filled' : 'outline'}
              onClick={() => openProvider(provider.key, provider.href)}
              fullWidth
              className={isPage ? styles.button : undefined}
            >
              {provider.label}
            </Button>
            {provider.key === 'google' && isMobile && (
              <div className={styles.googleHint}>
                {t(
                  'calendarSync.googleMobileHint',
                  'Sur téléphone, Google Agenda demande de passer par un ordinateur — ou copiez le lien ci-dessous.'
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      <div className={styles.urlSection}>
        <div className={styles.urlLabel}>
          {t('calendarSync.otherCopy', 'Autre agenda : copier le lien')}
        </div>
        <CopyButton value={icalUrl}>
          {({ copied, copy }) => (
            <Button
              leftSection={<IconLink size={16} />}
              color={copied ? 'teal' : undefined}
              onClick={() => {
                copy();
                trackPlanningSubscribeClicked({ provider: 'copy', context });
              }}
              variant="light"
              size="sm"
              fullWidth
            >
              {copied
                ? t('calendarSync.linkCopied', 'Lien copié !')
                : t('calendarSync.copyLink', 'Copier le lien iCal')}
            </Button>
          )}
        </CopyButton>
      </div>
    </div>
  );
};

export default CalendarSubscribeOptions;
